import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Trash2, AlertTriangle, X, Check, Loader2, RotateCcw, 
  DollarSign, User, Clock, MessageSquare
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';

// Quick void reasons
const VOID_REASONS = [
  { id: 'wrong_item', label: 'Wrong Item', icon: '❌' },
  { id: 'customer_changed', label: 'Customer Changed Mind', icon: '🔄' },
  { id: 'quality_issue', label: 'Quality Issue', icon: '⚠️' },
  { id: 'kitchen_error', label: 'Kitchen Error', icon: '👨‍🍳' },
  { id: 'out_of_stock', label: 'Out of Stock', icon: '📦' },
  { id: 'other', label: 'Other', icon: '📝' }
];

export default function VoidModal({ 
  open, 
  onClose, 
  item, 
  itemIndex,
  orderId,
  onVoidComplete 
}) {
  const { api, user } = useAuth();
  const [selectedReason, setSelectedReason] = useState(null);
  const [customReason, setCustomReason] = useState('');
  const [voidQuantity, setVoidQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [requiresManagerApproval, setRequiresManagerApproval] = useState(false);
  const [managerPin, setManagerPin] = useState('');

  // Check if item value requires manager approval (e.g., over £20)
  const itemTotal = item ? item.price * voidQuantity : 0;
  const needsApproval = itemTotal > 20;

  const handleVoid = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason');
      return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    if (needsApproval && !managerPin) {
      setRequiresManagerApproval(true);
      return;
    }

    setProcessing(true);
    try {
      const voidData = {
        item_index: itemIndex,
        menu_item_id: item.menu_item_id,
        item_name: item.name,
        quantity: voidQuantity,
        unit_price: item.price,
        total_value: itemTotal,
        reason: selectedReason,
        reason_text: selectedReason === 'other' ? customReason : VOID_REASONS.find(r => r.id === selectedReason)?.label,
        voided_by: user?.id,
        voided_by_name: user?.name,
        manager_pin: needsApproval ? managerPin : null
      };

      if (orderId) {
        // Void from existing order
        await api.post(`/orders/${orderId}/void-item`, voidData);
      }

      toast.success(`${item.name} voided`);
      onVoidComplete?.(itemIndex, voidQuantity, voidData);
      handleClose();
    } catch (error) {
      console.error('Void failed:', error);
      if (error.response?.data?.detail?.includes('Manager')) {
        toast.error('Invalid manager PIN');
      } else {
        toast.error('Failed to void item');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setCustomReason('');
    setVoidQuantity(1);
    setRequiresManagerApproval(false);
    setManagerPin('');
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Void Item
          </DialogTitle>
          <DialogDescription>
            Remove an item from the order with a reason
          </DialogDescription>
        </DialogHeader>

        {/* Item Details */}
        <div className="bg-slate-100 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">{item.name}</span>
            <span className="text-orange-500 font-bold">£{item.price.toFixed(2)}</span>
          </div>
          
          {/* Quantity to void */}
          <div className="flex items-center gap-3 mt-3">
            <Label className="text-sm text-slate-600">Quantity to void:</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setVoidQuantity(Math.max(1, voidQuantity - 1))}
                disabled={voidQuantity <= 1}
              >
                -
              </Button>
              <span className="w-8 text-center font-bold">{voidQuantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setVoidQuantity(Math.min(item.quantity, voidQuantity + 1))}
                disabled={voidQuantity >= item.quantity}
              >
                +
              </Button>
              <span className="text-sm text-slate-500">of {item.quantity}</span>
            </div>
          </div>

          <div className="text-sm text-slate-500 mt-2">
            Void total: <span className="font-bold text-red-500">£{itemTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Quick Reason Buttons */}
        <div className="space-y-2">
          <Label>Reason for void:</Label>
          <div className="grid grid-cols-2 gap-2">
            {VOID_REASONS.map(reason => (
              <Button
                key={reason.id}
                variant={selectedReason === reason.id ? 'default' : 'outline'}
                className={`justify-start ${selectedReason === reason.id ? 'bg-red-500 hover:bg-red-600' : ''}`}
                onClick={() => setSelectedReason(reason.id)}
                data-testid={`void-reason-${reason.id}`}
              >
                <span className="mr-2">{reason.icon}</span>
                {reason.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Custom Reason Input */}
        {selectedReason === 'other' && (
          <div className="mt-3">
            <Label>Please specify:</Label>
            <Textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter reason..."
              className="mt-1"
              data-testid="void-custom-reason"
            />
          </div>
        )}

        {/* Manager Approval Warning */}
        {needsApproval && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mt-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Manager Approval Required</p>
                <p className="text-sm text-yellow-700">
                  Voids over £20 require manager authorization
                </p>
                {requiresManagerApproval && (
                  <div className="mt-2">
                    <Label className="text-yellow-800">Manager PIN:</Label>
                    <Input
                      type="password"
                      value={managerPin}
                      onChange={(e) => setManagerPin(e.target.value)}
                      placeholder="Enter PIN"
                      className="mt-1"
                      maxLength={6}
                      data-testid="manager-pin-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={handleClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleVoid}
            disabled={processing || !selectedReason}
            className="flex-1 bg-red-600 hover:bg-red-700"
            data-testid="confirm-void-btn"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Void Item
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
